import { Box, CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import TablePaginationActions from '../../../base-components/TablePaginationActions';
import CustomModal from '../../../base-components/CustomModal';
import Popup from '../../../base-components/Popup';

const BASE_URL = process.env.REACT_APP_BASE_URL;
function StockManagement({ company_id, location_id, role }) {
  const [stock, setStock] = useState([]);
  const [products, setProducts] = useState([]);
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [isFailureAlert, setIsFailureAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (company_id && location_id) {
      getStock();
      getProducts();
    }
  }, [company_id, location_id]);

  const getProducts = async () => {
    try {
      const response = await fetch(`${BASE_URL}products?company_id=${company_id}&location_id=${location_id}&is_active=true`);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with products property
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts(data.products || []);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const getStock = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}productStock?company_id=${company_id}&location_id=${location_id}`);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with stock property
        if (Array.isArray(data)) {
          setStock(data);
        } else {
          setStock(data.stock || []);
        }
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAddStockModal = () => {
    setIsAddStockModalOpen(!isAddStockModalOpen);
    if (!isAddStockModalOpen) {
      resetForm();
    }
  };

  const resetForm = () => {
    setValues({
      product_id: '',
      quantity: 0,
      cost_per_unit: 0,
    });
    setSelectedProduct(null);
  };

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, setValues } = useFormik({
    initialValues: {
      product_id: '',
      quantity: 0,
      cost_per_unit: 0,
    },
    onSubmit: async (values, actions) => {
      await handleAddStock(values, actions);
    },
  });

  const handleAddStock = async (values, actions) => {
    if (!values.product_id || values.quantity <= 0 || values.cost_per_unit <= 0) {
      setIsFailureAlert(true);
      setFailureMessage('Please fill all required fields with valid values');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
      return;
    }

    setButtonLoading(true);
    try {
      const sessionStr = sessionStorage.getItem('user-info');
      const session = JSON.parse(sessionStr);
      const created_by = session.user._id;

      const response = await fetch(`${BASE_URL}productStock/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id,
          location_id,
          product_id: values.product_id,
          quantity: parseInt(values.quantity),
          cost_per_unit: parseFloat(values.cost_per_unit),
          created_by,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setIsSuccessAlert(true);
        setSuccessMessage('Stock added successfully');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsSuccessAlert(false);
        }, 3000);
        getStock();
        toggleAddStockModal();
        resetForm();
      } else {
        setIsFailureAlert(true);
        setFailureMessage(data.message || 'Failed to add stock');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
      }
    } catch (error) {
      setIsFailureAlert(true);
      setFailureMessage('Error adding stock');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
      actions.setSubmitting(false);
    }
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setValues({ ...values, product_id: productId });
    const product = products.find((p) => p._id === productId);
    setSelectedProduct(product);
    // Cost per unit is now entered manually when adding stock
  };

  const paginatedStock =
    !stock || stock.length === 0
      ? []
      : rowsPerPage === -1
      ? stock
      : stock.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? 'success' : 'failure'}
          message={isSuccessAlert ? successMessage : failureMessage}
        />
      )}

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-xl text-gx-ink-soft">Stock Management</h2>
        <button
          onClick={toggleAddStockModal}
          className="gx-cta"
        >
          Add Stock
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-20">
          <CircularProgress
            thickness={4}
            size={35}
            sx={{
              'svg circle': {
                stroke: 'url(#gradientColors)',
              },
            }}
          />
        </div>
      ) : stock && stock.length !== 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5 animate-fadeIn overflow-y-auto overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Current Stock</TableCell>
                <TableCell>Reorder Level</TableCell>
                <TableCell>Last Restocked</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedStock.map((item, index) => (
                <TableRow hover key={index}>
                  <TableCell>{item.product_id?.name || 'N/A'}</TableCell>
                  <TableCell>{item.product_id?.category || 'N/A'}</TableCell>
                  <TableCell>{item.product_id?.sku || 'N/A'}</TableCell>
                  <TableCell>
                    <span className={item.quantity <= item.reorder_level ? 'text-red-600 font-bold' : ''}>
                      {item.quantity}
                    </span>
                  </TableCell>
                  <TableCell>{item.reorder_level}</TableCell>
                  <TableCell>
                    {item.last_restocked_at
                      ? new Date(item.last_restocked_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    <div
                      className={`font-bold p-2 px-4 rounded-full text-center ${
                        item.quantity <= item.reorder_level
                          ? 'bg-red-700 text-white'
                          : item.quantity <= item.reorder_level * 1.5
                          ? 'bg-yellow-700 text-white'
                          : 'bg-green-700 text-white'
                      }`}
                    >
                      {item.quantity <= item.reorder_level
                        ? 'Low Stock'
                        : item.quantity <= item.reorder_level * 1.5
                        ? 'Medium Stock'
                        : 'In Stock'}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={7}
                  count={stock.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  slotProps={{
                    select: {
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    },
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-10 rounded-lg m-10">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">No Stock Records Found</h3>
        </div>
      )}

      {/* Add Stock Modal */}
      <CustomModal isModal={isAddStockModalOpen} handleModal={toggleAddStockModal} size="xl">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Add Stock</h1>
        <form onSubmit={handleSubmit} className="text-gx-ink-soft m-5" autoComplete="off">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
            <div className="w-full sm:col-span-2">
              <label>
                Product <span className="text-red-600">*</span>
              </label>
              <select
                name="product_id"
                value={values.product_id}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.product_id && touched.product_id ? 'border-red-600' : ''
                }`}
                onChange={handleProductChange}
                onBlur={handleBlur}
              >
                <option value="">Select Product</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} - {product.sku} ({product.category})
                  </option>
                ))}
              </select>
              {touched.product_id && errors.product_id ? (
                <p className="text-red-500 pl-2 italic">{errors.product_id}</p>
              ) : null}
              {selectedProduct && (
                <div className="mt-2 p-2 bg-gx-canvas rounded">
                  <p className="text-sm">
                    <span className="font-semibold">Unit Price (Selling):</span> Rs. {selectedProduct.unit_price || 0}
                  </p>
                  <p className="text-xs text-gx-ink-soft mt-1">Enter the cost per unit for this stock addition below</p>
                </div>
              )}
            </div>

            <div className="w-full">
              <label>
                Quantity <span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="quantity"
                placeholder="Enter Quantity"
                value={values.quantity}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.quantity && touched.quantity ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
                min="1"
              />
              {touched.quantity && errors.quantity ? (
                <p className="text-red-500 pl-2 italic">{errors.quantity}</p>
              ) : null}
            </div>

            <div className="w-full">
              <label>
                Cost Per Unit <span className="text-red-600">*</span>
              </label>
              <input
                type="number"
                name="cost_per_unit"
                placeholder="Enter Cost Per Unit"
                value={values.cost_per_unit}
                className={`border border-gx-border bg-gx-canvas w-full p-2 rounded-xl outline-0 ${
                  errors.cost_per_unit && touched.cost_per_unit ? 'border-red-600' : ''
                }`}
                onChange={handleChange}
                onBlur={handleBlur}
                min="0"
                step="0.01"
              />
              {touched.cost_per_unit && errors.cost_per_unit ? (
                <p className="text-red-500 pl-2 italic">{errors.cost_per_unit}</p>
              ) : null}
            </div>

            {values.quantity > 0 && values.cost_per_unit > 0 && (
              <div className="w-full sm:col-span-2 p-3 bg-blue-50 rounded">
                <p className="font-semibold text-blue-800">
                  Total Cost: Rs. {(values.quantity * values.cost_per_unit).toFixed(2)}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  This will create an expense entry and transaction record.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end items-center pt-3 mt-3 border-t">
            <button
              type="button"
              onClick={toggleAddStockModal}
              className="gx-cta-secondary mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              className="gx-cta disabled:opacity-50"
            >
              {buttonLoading ? 'Adding...' : 'Add Stock'}
            </button>
          </div>
        </form>
      </CustomModal>
    </>
  );
}

export default StockManagement;

